import { Leaf, ShieldCheck, Award, Clock, Users, ThumbsUp } from "lucide-react";

const features = [
  {
    icon: Leaf,
    title: "Eco-Friendly",
    description: "We use only green-certified, non-toxic cleaning products safe for your family and pets.",
    color: "bg-[#3B82F6]/10",
    iconColor: "text-[#2563EB]",
  },
  {
    icon: ShieldCheck,
    title: "Vetted Staff",
    description: "Every team member is background-checked, trained, and insured for your peace of mind.",
    color: "bg-[#2563EB]/10",
    iconColor: "text-[#2563EB]",
  },
  {
    icon: Award,
    title: "100% Guarantee",
    description: "Not satisfied? We'll re-clean for free. Your happiness is our top priority.",
    color: "bg-amber-100",
    iconColor: "text-amber-600",
  },
  {
    icon: Clock,
    title: "Flexible Scheduling",
    description: "Book weekly, bi-weekly, or one-time cleans. We work around your schedule.",
    color: "bg-purple-100",
    iconColor: "text-purple-600",
  },
  {
    icon: Users,
    title: "Dedicated Teams",
    description: "The same trusted cleaners every visit, so you always know who's in your space.",
    color: "bg-rose-100",
    iconColor: "text-rose-600",
  },
  {
    icon: ThumbsUp,
    title: "Transparent Pricing",
    description: "No hidden fees, no surprises. Get your quote upfront and know exactly what you'll pay.",
    color: "bg-blue-100",
    iconColor: "text-sky-600",
  },
];

const WhyChooseUs = () => {
  return (
    <section
      id="why-us"
      data-testid="why-choose-us-section"
      className="py-16 md:py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#2563EB] font-medium text-sm uppercase tracking-wider">
            Why River City?
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 mb-4">
            The Difference is in the
            <br />
            <span className="gradient-text">Details</span>
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            We're not just cleaners—we're your partners in creating a healthier,
            happier space.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="group p-6 bg-white rounded-2xl border border-slate-100 hover:shadow-lg hover:border-slate-200 transition-all duration-300"
                data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                >
                  <Icon className={`w-7 h-7 ${feature.iconColor}`} strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* Stats Bar */}
        <div className="mt-16 p-8 bg-gradient-to-r from-[#2563EB] to-[#0891b2] rounded-3xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div data-testid="stat-clients">
              <p className="text-4xl font-bold mb-1">500+</p>
              <p className="text-white/80 text-sm">Happy Clients</p>
            </div>
            <div data-testid="stat-cleanings">
              <p className="text-4xl font-bold mb-1">10K+</p>
              <p className="text-white/80 text-sm">Cleanings Done</p>
            </div>
            <div data-testid="stat-years">
              <p className="text-4xl font-bold mb-1">8+</p>
              <p className="text-white/80 text-sm">Years Experience</p>
            </div>
            <div data-testid="stat-rating">
              <p className="text-4xl font-bold mb-1">4.9</p>
              <p className="text-white/80 text-sm">Average Rating</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
